import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AskSita() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "sita", text: "Hello — welcome to Sita. Ask me about the supper club, catering, or having us cook for you." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "you", text }]);
    setBusy(true);
    const reply = { role: "sita", text: "" };
    setMessages((m) => [...m, reply]);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionRef.current }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.delta) {
            reply.text += payload.delta;
            setMessages((m) => [...m.slice(0, -1), { ...reply }]);
          }
          if (payload.session_id) sessionRef.current = payload.session_id;
          if (payload.error) {
            setMessages((m) => [...m.slice(0, -1), { role: "sita", text: "Something went wrong on our end — try again in a moment." }]);
          }
        }
      }
    } catch (err) {
      setMessages((m) => [...m.slice(0, -1), { role: "sita", text: "Something went wrong on our end — try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {open && (
        <div
          data-testid="ask-sita-panel"
          className="fixed bottom-24 right-4 z-50 flex w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-forest/15 bg-cream shadow-2xl md:right-6"
        >
          <div className="flex items-center justify-between bg-deepforest px-5 py-4">
            <div>
              <p className="font-display text-lg italic text-cream">Ask Sita</p>
              <p className="text-[11px] uppercase tracking-[0.16em] text-cream/60">AI concierge · powered by Claude</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="ask-sita-close-button"
              aria-label="Close Ask Sita"
              className="cursor-pointer rounded-full p-1.5 text-cream/80 transition-colors hover:text-gold"
            >
              <X size={18} />
            </button>
          </div>
          <div ref={scrollRef} data-testid="ask-sita-messages" className="h-80 space-y-3 overflow-y-auto px-4 py-4">
            {messages.map((m, i) => (
              <div key={i} data-testid={m.role === "you" ? "ask-sita-user-message" : "ask-sita-reply"} className={`flex ${m.role === "you" ? "justify-end" : "justify-start"}`}>
                <p
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "you" ? "bg-terracotta text-cream" : "border border-forest/10 bg-white/70 text-ink"
                  }`}
                >
                  {m.text || "…"}
                </p>
              </div>
            ))}
          </div>
          <form onSubmit={send} className="flex items-center gap-2 border-t border-forest/10 p-3" data-testid="ask-sita-form">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about menus, seats, catering…"
              data-testid="ask-sita-input"
              className="flex-1 rounded-full border border-forest/20 bg-white/60 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-gold/70"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="ask-sita-send-button"
              aria-label="Send message"
              className="cursor-pointer rounded-full bg-terracotta p-2.5 text-cream transition-colors hover:bg-gold hover:text-deepforest disabled:opacity-60"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="ask-sita-toggle"
        aria-label={open ? "Close Ask Sita" : "Ask Sita"}
        className="fixed bottom-5 right-4 z-50 flex h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-terracotta text-cream shadow-lg transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold hover:text-deepforest md:right-6"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
    </>
  );
}
